/** Shared login-screen building blocks (extracted from LoginPage for reuse + size). */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, getUser, getToken, roleHome } from "@/lib/api";
import { getDeviceId } from "@/lib/secureStore";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { Wifi, WifiOff, Headphones, ChevronRight, Phone, Mail, ShieldCheck, ChevronDown, Globe } from "lucide-react";

export const deviceHeaders = () => ({ "X-Device-Id": getDeviceId() });

export const WHATSAPP_SUPPORT_NUMBER = "919690181181";
const SUPPORT_PHONE_DISPLAY = "+91 9690 181 181";
const SUPPORT_PHONE_TEL = "+919690181181";
const SUPPORT_EMAIL = "support@animalmela.com";

export const FIELD_IMG =
  "https://images.unsplash.com/photo-1632083000159-8e17b5ae7fd5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxhZ3JpY3VsdHVyZSUyMHRlY2glMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODI3MzUxNTJ8MA&ixlib=rb-4.1.0&q=85";

/** Map coded auth errors from backend/offline store to translated messages. */
export function mapAuthError(t, detail, kind = "password") {
  if (!detail || typeof detail !== "string") return t("msg.loginFailed");
  if (/^(account_locked|pin_locked|locked):/.test(detail)) {
    return t("auth.lockedMsg", { n: detail.split(":")[1] || 15 });
  }
  if (detail.startsWith("invalid_credentials:")) return t("auth.invalidCredsLeft", { n: detail.split(":")[1] });
  if (detail.startsWith("invalid_pin:")) return t("auth.invalidPinLeft", { n: detail.split(":")[1] });
  if (detail.startsWith("invalid:")) {
    const n = detail.split(":")[1];
    return kind === "pin" ? t("auth.invalidPinLeft", { n }) : t("auth.invalidCredsLeft", { n });
  }
  if (detail === "password_login_disabled") return t("auth.methodDisabled");
  if (detail === "pin_login_disabled") return t("auth.methodDisabled");
  if (detail === "otp_login_disabled") return t("auth.otpDisabledMsg");
  if (detail === "pin_not_available") return t("auth.pinNotOnDevice");
  if (detail === "not_available") return kind === "pin" ? t("auth.pinNotOnDevice") : t("auth.offlineNotAvailable");
  return detail;
}

/** Online / Offline mode indicator shown on the login screen. */
export function ModeChip() {
  const { t } = useT();
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const upd = () => setOnline(navigator.onLine);
    window.addEventListener("online", upd);
    window.addEventListener("offline", upd);
    return () => { window.removeEventListener("online", upd); window.removeEventListener("offline", upd); };
  }, []);
  return (
    <div
      data-testid="auth-mode-chip"
      className={`mx-auto mb-5 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-bold ring-1 ${
        online ? "bg-[#F3FFF5] text-[#0f6b3a] ring-[#25D366]/30" : "bg-amber-50 text-amber-800 ring-amber-300"
      }`}
    >
      {online ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
      {online ? t("auth.onlineMode") : t("auth.offlineMode")}
      <span className={`w-2 h-2 rounded-full ${online ? "bg-[#25D366]" : "bg-amber-500"}`} />
    </div>
  );
}

/** Offline login — resume the last signed-in session without internet. */
export function OfflineContinueCard() {
  const { t } = useT();
  const nav = useNavigate();
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const upd = () => setOnline(navigator.onLine);
    window.addEventListener("online", upd);
    window.addEventListener("offline", upd);
    return () => { window.removeEventListener("online", upd); window.removeEventListener("offline", upd); };
  }, []);
  const user = getUser();
  if (online || !user || !getToken()) return null;
  return (
    <div className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 p-4 flex items-center gap-3" data-testid="offline-continue-card">
      <span className="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" />
      <div className="flex-1 min-w-0 text-sm font-semibold text-amber-900">
        {t("offline.continueAs", { name: user.name || user.mobile })}
      </div>
      <Button size="sm" className="am-btn-primary rounded-full shrink-0" onClick={() => nav(roleHome(user.role))} data-testid="offline-continue-btn">
        {t("offline.continueBtn")}
      </Button>
    </div>
  );
}

function SupportSection() {
  const { t } = useT();
  return (
    <div className="mt-5 w-full rounded-2xl bg-[#F3FFF5] p-4 sm:p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_6px_20px_rgba(37,211,102,0.10)]" data-testid="auth-support-card">
      {/* Row 1 — Need help + WhatsApp CTA */}
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-full bg-[#25D366]/12 text-[#0f6b3a] grid place-items-center shrink-0">
          <Headphones className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-foreground leading-tight">{t("auth.needHelp")}</div>
          <div className="text-xs text-muted-foreground leading-tight mt-0.5">{t("auth.teamHere")}</div>
        </div>
        <a
          href={`https://wa.me/${WHATSAPP_SUPPORT_NUMBER}`}
          target="_blank"
          rel="noreferrer"
          data-testid="auth-whatsapp-support"
          aria-label={t("auth.chatOnWhatsapp")}
          className="group inline-flex items-center gap-1.5 rounded-full bg-white ring-1 ring-[#25D366]/25 hover:ring-[#25D366]/60 text-[#0f6b3a] text-xs font-semibold px-3 py-2 shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.97]"
        >
          <span className="w-5 h-5 rounded-full bg-[#25D366] text-white grid place-items-center transition-transform duration-200 group-hover:scale-110">
            <WhatsAppIcon className="w-3.5 h-3.5" color="#ffffff" />
          </span>
          <span>{t("auth.chatOnWhatsapp")}</span>
          <ChevronRight className="w-3.5 h-3.5 opacity-60" />
        </a>
      </div>

      <div className="my-4 h-px bg-[#25D366]/15" />

      {/* Row 2 — Call + Email */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <a
          href={`tel:${SUPPORT_PHONE_TEL}`}
          data-testid="auth-call-support"
          className="group flex items-center gap-3 rounded-xl px-2 py-1.5 -mx-2 hover:bg-white/60 transition-colors duration-200"
        >
          <span className="w-10 h-10 rounded-full bg-[#25D366]/12 text-[#0f6b3a] grid place-items-center shrink-0 transition-transform duration-200 group-hover:scale-105">
            <Phone className="w-4 h-4" />
          </span>
          <span className="leading-tight min-w-0">
            <span className="block text-sm font-semibold text-foreground">{t("auth.callSupport")}</span>
            <span className="block text-xs font-mono text-muted-foreground truncate">{SUPPORT_PHONE_DISPLAY}</span>
          </span>
        </a>
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          data-testid="auth-email-support"
          className="group flex items-center gap-3 rounded-xl px-2 py-1.5 -mx-2 hover:bg-white/60 transition-colors duration-200"
        >
          <span className="w-10 h-10 rounded-full bg-[#25D366]/12 text-[#0f6b3a] grid place-items-center shrink-0 transition-transform duration-200 group-hover:scale-105">
            <Mail className="w-4 h-4" />
          </span>
          <span className="leading-tight min-w-0">
            <span className="block text-sm font-semibold text-foreground">{t("auth.emailUs")}</span>
            <span className="block text-xs text-muted-foreground truncate">{SUPPORT_EMAIL}</span>
          </span>
        </a>
      </div>
    </div>
  );
}

function TrustBadge() {
  const { t } = useT();
  return (
    <div className="mt-4 flex items-center justify-center gap-2 text-xs text-[#0f6b3a]/80" data-testid="auth-trust-badge">
      <ShieldCheck className="w-4 h-4" />
      <span className="font-medium">{t("auth.dataSafe")}</span>
    </div>
  );
}

export const WhatsAppSupport = () => (
  <>
    <SupportSection />
    <TrustBadge />
  </>
);

/**
 * Mobile number input with a `+91 ▼` chip on the left and a phone-icon prefix,
 * matching the AnimalMela sign-in mock. India-only for now — the chevron is
 * visual polish; there is no country dropdown yet.
 */
export function MobileNumberField({ value, onChange, testId, placeholder = "Enter 10-digit mobile number" }) {
  return (
    <div className="relative flex items-stretch rounded-full bg-white ring-1 ring-border shadow-[0_1px_2px_rgba(16,24,40,0.04)] focus-within:ring-primary focus-within:ring-2 overflow-hidden">
      <span className="flex items-center gap-1.5 pl-3 pr-2 my-1 mr-1 rounded-full bg-[#F3FFF5] text-[#0f6b3a] text-sm font-semibold">
        <span className="w-6 h-6 rounded-full bg-[#25D366]/15 grid place-items-center">
          <Phone className="w-3 h-3" />
        </span>
        <span className="tracking-wide">+91</span>
        <ChevronDown className="w-3.5 h-3.5 opacity-70" />
      </span>
      <input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
        placeholder={placeholder}
        className="flex-1 min-w-0 bg-transparent outline-none px-2 pr-4 text-base placeholder:text-muted-foreground/70"
        inputMode="numeric"
        maxLength={15}
      />
    </div>
  );
}

/** India-only launch — shown to visitors outside India (Geo Access Control). */
export function GeoBlockedScreen({ onAdminLogin }) {
  const [lead, setLead] = useState({ name: "", email: "", country: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!lead.name.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(lead.email) || !lead.country.trim()) {
      toast.error("Please fill name, a valid email and your country");
      return;
    }
    setBusy(true);
    try {
      await api.post("/geo/notify", lead);
      setSent(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save — please try again");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background px-4" data-testid="geo-blocked-screen">
      <div className="am-card p-8 max-w-md w-full text-center">
        <div className="w-14 h-14 rounded-full bg-accent grid place-items-center mx-auto mb-4">
          <Globe className="w-7 h-7 text-primary" />
        </div>
        <h1 className="font-display text-2xl font-extrabold">AnimalMela is currently available only in India.</h1>
        <p className="text-muted-foreground mt-2">We will launch in your country soon.</p>

        {sent ? (
          <p className="mt-6 font-semibold text-primary" data-testid="geo-lead-thanks">
            Thank you! We will notify you when AnimalMela launches in your country.
          </p>
        ) : (
          <div className="mt-6 space-y-3 text-left">
            <Input data-testid="geo-lead-name" placeholder="Name" className="am-input" value={lead.name}
              onChange={(e) => setLead({ ...lead, name: e.target.value })} />
            <Input data-testid="geo-lead-email" type="email" placeholder="Email" className="am-input" value={lead.email}
              onChange={(e) => setLead({ ...lead, email: e.target.value })} />
            <Input data-testid="geo-lead-country" placeholder="Country" className="am-input" value={lead.country}
              onChange={(e) => setLead({ ...lead, country: e.target.value })} />
            <Button data-testid="geo-notify-btn" className="am-btn-primary w-full" onClick={submit} disabled={busy}>
              {busy ? "…" : "Notify Me"}
            </Button>
          </div>
        )}

        <button onClick={onAdminLogin} className="mt-6 text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
          data-testid="geo-admin-login-link">
          Admin sign in
        </button>
      </div>
    </div>
  );
}
